<script setup lang="ts">
import type { ApiUserInfoSelf } from '~/types'

const props = defineProps<{ userInfo: Partial<ApiUserInfoSelf>, isPreview?: boolean }>()

const { userInfo: userInfoQuery } = useQuery()
const { data: user, isLoading } = userInfoQuery.userByEmail(props.userInfo.userEmail)
</script>

<template>
  <div class="flex flex-col border border-gray-300 rounded-md p-3 w-full max-w-none xl:max-w-[500px] bg-gray-50 justify-center">
    <template v-if="!isLoading && user">
      <div class="flex flex-col sm:flex-row gap-2 mb-4">
        <n-image
          v-if="userInfo.userPhoto"
          class="rounded-md bg-gray-300 border-2 border-gray-300 w-[130px] h-[130px] justify-center"
          object-fit="contain"
          :src="userInfo.userPhoto.path"
          :alt="user.name ?? undefined"
        />
        <div v-else-if="isPreview" class="flex items-center justify-center w-[120px] h-[120px] rounded-md bg-gray-300">
          <Icon name="material-symbols:frame-person" class="text-6xl" />
        </div>

        <div class="flex flex-col" :class="{ 'sm:mt-4 sm:ml-1': Boolean(userInfo.userPhoto || isPreview) }">
          <h1 class="text-lg font-bold">
            {{ user.name }}
          </h1>
          <p v-if="userInfo.function" class="text-sm font-medium text-gray-500">
            {{ userInfo.function }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <div v-if="userInfo.userEmail" class="listContainer">
          <Icon name="material-symbols:mail-outline-rounded" class="listIcon" size="24" />
          <a class="listText" :href="`mailto:${userInfo.userEmail}`">
            {{ userInfo.userEmail }}
          </a>
        </div>

        <div v-if="userInfo.phone" class="listContainer">
          <Icon name="material-symbols:call-outline-rounded" class="listIcon" size="24" />
          <a class="listText" :href="`tel:${userInfo.phone}`">
            {{ userInfo.phone }}
          </a>
        </div>

        <div v-if="userInfo.mobilePhone" class="listContainer">
          <Icon name="material-symbols:phone-android-outline-rounded" class="listIcon" size="24" />
          <a class="listText" :href="`tel:${userInfo.mobilePhone}`">
            {{ userInfo.mobilePhone }}
          </a>
        </div>

        <div v-if="userInfo.hasLanguageProficiencyIn && userInfo.hasLanguageProficiencyIn.length > 0" class="listContainer">
          <Icon name="material-symbols:language" class="listIcon" size="24" />
          <span class="break-normal">{{ $t('userInfo.field.hasLanguageProficiencyIn.name') }}:</span>
          <p>
            {{ userInfo.hasLanguageProficiencyIn.map(code => $t(`customer.language.${code}`)).join(', ') }}
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <n-skeleton height="150px" width="100%" />
    </template>
  </div>
</template>

<style scoped>
.listContainer {
  @apply flex items-center gap-2
}
.listIcon {
  @apply text-gray-500 text-xl;
}
.listText {
  @apply text-sm text-hos-blue;
}
</style>
